/*
 * Tupaia
 * Copyright (c) 2017 - 2020 Beyond Essential Systems Pty Ltd
 */
import React from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import { format } from "date-fns";
import MuiPaper from "@material-ui/core/Paper";
import parse from "html-react-parser";
import Typography from "@material-ui/core/Typography";
import MuiBox from "@material-ui/core/Box";
import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@material-ui/core/IconButton";
import { useConfig } from "../../config/configContext";

const ResultsBox = styled(MuiPaper)`
  position: relative;
  padding: 1rem 2rem;
  margin-bottom: 1rem;
`;

const CloseButton = styled(IconButton)`
  position: absolute;
  top: 0;
  right: 0;
`;

const BaseCard = ({ onClose, children }) => {
  return (
    <ResultsBox elevation={0}>
      <CloseButton onClick={onClose}>
        <CloseIcon />
      </CloseButton>
      {children}
    </ResultsBox>
  );
};

const DEFAULT_RESULT_DESCRIPTIONS = {
  positive:
    "We have detected COVID-19 in your sample. If you have not been directly contacted, please contact the Ministry of Health and Medical Services for more information.",
  negative:
    "We have NOT detected SARS-CoV-2 (the virus that causes COVID-19) in your sample.",
  "test failed":
    "We have been unable to determine a result. You should get re-tested at the earliest opportunity.",
  inconclusive:
    "We have been unable to determine a result. You should get re-tested at the earliest opportunity.",
  "no results yet":
    "Please wait more time and then check again. Test results can take from 24 hours to several days to be available.",
};

const useResultDescription = (result) => {
  const { content } = useConfig();
  const { resultDescriptions = {} } = content;
  const key = result?.toLowerCase();
  return resultDescriptions[key] || DEFAULT_RESULT_DESCRIPTIONS[key];
};

export const ResultCard = ({ initials, result, date, testId, onClose }) => {
  const niceDate = format(new Date(date), "d MMM yyyy");
  const description = useResultDescription(result);

  return (
    <BaseCard onClose={onClose}>
      <Typography variant="h6" color="primary" gutterBottom>
        {testId}
      </Typography>
      <Typography variant="subtitle1">Result</Typography>
      <Typography variant="h4" data-testid="result">
        {result}
      </Typography>
      {description && (
        <Typography color="textSecondary" variant="body1" paragraph>
          {parse(description)}
        </Typography>
      )}
      <MuiBox display="flex" justifyContent="space-between" mt={1} mb={1}>
        <MuiBox>
          <Typography variant="subtitle2">Initials</Typography>
          <Typography variant="h6">{initials}</Typography>
        </MuiBox>
        <MuiBox>
          <Typography variant="subtitle2">Test ID</Typography>
          <Typography variant="h6">{testId}</Typography>
        </MuiBox>
        <MuiBox>
          <Typography variant="subtitle2">Date of swab</Typography>
          <Typography variant="h6">{niceDate}</Typography>
        </MuiBox>
      </MuiBox>
    </BaseCard>
  );
};

ResultCard.propTypes = {
  initials: PropTypes.string.isRequired,
  testId: PropTypes.string.isRequired,
  result: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

ResultCard.defaultProps = {
  result: "No Results Yet",
};

const NoResultsBox = styled.div`
  position: relative;
  text-align: center;
  padding: 1rem 2rem;

  img {
    width: 4.375rem;
    margin-bottom: 0.625rem;
  }
`;

export const NoResultCard = ({ value, onClose }) => {
  return (
    <BaseCard onClose={onClose}>
      <NoResultsBox>
        <img src="no-results-icon.svg" alt="no results" />
        <Typography data-testid="result">
          No results found for the search
        </Typography>
        <Typography>&quot;{value}&quot;</Typography>
      </NoResultsBox>
    </BaseCard>
  );
};

NoResultCard.propTypes = {
  value: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export const ErrorResultCard = ({ heading, text, onClose }) => {
  return (
    <BaseCard onClose={onClose}>
      <Typography variant="h6" data-testid="result">
        {heading}
      </Typography>
      <Typography>{text}</Typography>
    </BaseCard>
  );
};

ErrorResultCard.propTypes = {
  heading: PropTypes.string,
  text: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

ErrorResultCard.defaultProps = {
  heading: "Error",
  text: "There was an error. Please refresh the page and try again.",
};
